import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Select, Tag } from 'ui';
import { useT } from 'hooks/Locale/useT';
import { Table } from 'antd';
import moment from 'moment';
import { config } from 'config/config';
import { TagStatus } from 'ui/Tag/types';
import useQueryApiClient from 'utils/useApiClient';

interface ResultsModalInterface {
    open: boolean;
    onCancel: React.Dispatch<React.SetStateAction<boolean>>;
    id: number;
    onSuccess?: () => void;
}

const SessionAttendanceChangeModal: React.FC<ResultsModalInterface> = ({
    open,
    onCancel,
    id,
    onSuccess,
}) => {
    const t = useT();
    const [attendances, setAttendances] = useState([]);
    const [attempts, setAttempts] = useState([]);
    const [selectedAttempts, setSelectedAttempts] = useState<Record<number, any[]>>({});

    const prevOpenRef = useRef(open);

    useEffect(() => {
        if (open && !prevOpenRef.current) {
            fetch();
            fetchAttempts();
        }
        prevOpenRef.current = open;
    }, [open]);

    const { fetch, isLoading } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: `/api/v1/courses/learning-program/results/${id}/attendances`,
            method: 'GET',
        },
        onSuccess: (response) => {
            setAttendances(response.data);
        },
    });

    const { fetch: fetchAttempts } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: `/api/v1/courses/learning-program/results/${id}/attempts`,
            method: 'GET',
        },
        onSuccess: (response) => {
            setAttempts(response.data);
        },
    });

    const { appendData } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: `/api/v1/courses/learning-program/results/${id}/attendances`,
            method: 'POST',
        },
        onSuccess: () => {
            if (onSuccess) {
                onSuccess();
            }
        },
    });

    const handleCancel = () => {
        onCancel(false);
        setAttendances([]);
        setAttempts([]);
        setSelectedAttempts({});
    };

    const handleSelectChange = (recordId: number, values: any[]) => {
        setSelectedAttempts((prev) => ({
            ...prev,
            [recordId]: values,
        }));
    };

    const handleSaveAttempts = (recordId: number) => {
        const selected = selectedAttempts[recordId];
        if (selected) {
            appendData({
                attendance: recordId,
                attempts: selected,
            });
        }
    };

    const columns = [
        {
            title: t('general.title'),
            dataIndex: 'title',
        },
        {
            title: t('general.status'),
            dataIndex: 'status',
            render: (status: TagStatus) => (status ? <Tag status={status} /> : null),
        },
        {
            title: t('lectures.date'),
            dataIndex: 'session_date',
            render: (date: string) => moment(date).format(config.defaultDateFormat + ' HH:mm'),
        },
        {
            title: t('program.attempt'),
            render: (record: any) => {
                return (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Select
                            defaultValue={record.attempts}
                            value={selectedAttempts[record.id] ?? record.attempts}
                            mode="multiple"
                            isFormItem={false}
                            manualOptions={attempts}
                            onChange={(values) => handleSelectChange(record.id, values)}
                            style={{ width: 100 }}
                        />
                        <Button
                            onClick={() => handleSaveAttempts(record.id)}
                            message="general.save"
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <Modal
            open={open}
            width={'80%'}
            onCancel={handleCancel}
            className="add_remove_activity"
            title={t('program.attendance_update_modal')}
            destroyOnClose={true}
            footer={<Button onClick={handleCancel} message="general.close" />}
        >
            <Table
                loading={isLoading}
                columns={columns}
                dataSource={attendances}
                rowKey={'id'}
                pagination={{ position: [] }}
            />
        </Modal>
    );
};

export default SessionAttendanceChangeModal;
